import type { ReactNode } from 'react';
import { Loading } from 'react-admin';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { hasAccess, type AccessRequirement } from './permissions';
import { useAccessContext } from './useAccessContext';

/** Clean "not authorized" page shown to authenticated-but-insufficient users. */
export const NotAuthorized = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6, px: 2 }}>
    <Card sx={{ maxWidth: 520 }}>
      <CardContent>
        <Typography variant="h5" component="h1" gutterBottom>
          Not authorized
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Your account does not have access to this page. If you think this is a
          mistake, contact an administrator.
        </Typography>
      </CardContent>
    </Card>
  </Box>
);

export interface RequireAccessProps extends AccessRequirement {
  children: ReactNode;
}

/**
 * Route guard for authenticated-but-insufficient users. Declares the access a
 * route needs (capability and/or roles, plus optional region) and either renders
 * its children or a "not authorized" page. Unauthenticated users never reach
 * here — the Cognito login (0.2) handles them.
 *
 * Every Phase 1 surface gates with one wrapper, e.g.:
 *   <RequireAccess capability="payouts:write" region="UK"><Payouts /></RequireAccess>
 */
export const RequireAccess = ({
  children,
  ...requirement
}: RequireAccessProps) => {
  const { role, accessibleRegions, isLoading } = useAccessContext();

  if (isLoading) return <Loading />;
  if (!hasAccess({ role, accessibleRegions }, requirement)) {
    return <NotAuthorized />;
  }
  return <>{children}</>;
};
